import { useMemo } from "react";

import { useToggle } from "hooks/useToggle";
import Tree from "ds/components/Tree";
import { AccessResult } from "utils/access";
import HistoryEntryWrapper from "components/Run/elements/HistoryEntryWrapper";
import HistoryEntryLabel from "components/Run/elements/HistoryEntryLabel";

import RunDependencyBadge from "./RunDependencyBadge";
import { RunDependenciesEntryEvaluationProps, RunHistoryEntryType } from "../../types";
import { CombinedDependencies, isFinished } from "./utils";
import DependenciesEntryExternalDependency from "./ExternalDependency";
import DependenciesEntryRunDependency from "./RunDependency";

type DependenciesEntryProps = RunDependenciesEntryEvaluationProps & {
  dependencies: CombinedDependencies;
  isSimpleView: boolean;
  canSkipExternalDependencies: AccessResult;
};

const DependenciesEntry = ({
  dependencies,
  isSimpleView,
  canSkipExternalDependencies,
  evaluationState,
  evaluationTimestamp,
}: DependenciesEntryProps) => {
  const [isExpanded, toggle] = useToggle(true);

  const jsxList = useMemo(() => {
    let processedList = dependencies;

    if (isSimpleView) {
      processedList = processedList.filter((item) => !isFinished(item.state));
    }

    return processedList.map((item) => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (item.__typename) {
        case "RunDependency":
          return <DependenciesEntryRunDependency key={item.id} item={item} />;
        case "RunExternalDependency":
          return (
            <DependenciesEntryExternalDependency
              key={item.id}
              item={item}
              canSkipExternalDependencies={canSkipExternalDependencies}
            />
          );
        default:
          return null;
      }
    });
  }, [dependencies, isSimpleView, canSkipExternalDependencies]);

  const label = (
    <>
      <HistoryEntryLabel>
        <strong>Dependencies</strong> evaluated to
      </HistoryEntryLabel>
      <RunDependencyBadge state={evaluationState} />
    </>
  );

  return (
    <HistoryEntryWrapper
      label={label}
      timestamp={evaluationTimestamp}
      state={RunHistoryEntryType.Dependencies}
      isOpen={isExpanded}
      onClick={() => toggle()}
      terminal
    >
      {isExpanded && <Tree padding="0 0 0 large">{jsxList}</Tree>}
    </HistoryEntryWrapper>
  );
};

export default DependenciesEntry;
